/**
 * Firebase Authentication Service
 *
 * Provides methods for Firebase authentication operations:
 * - Email/password signup and login
 * - Google OAuth login
 * - Logout
 * - Auth state observation
 * - ID token retrieval
 */

import {
  type User as FirebaseUserType,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  GoogleAuthProvider,
  updateProfile,
  onAuthStateChanged,
  type Unsubscribe,
} from 'firebase/auth'

import { getFirebaseAuth } from '@/lib/firebase'
import type {
  LoginCredentials,
  SignupCredentials,
  FirebaseUser,
  AuthError,
} from '../types/auth.types'

/**
 * Google OAuth provider
 */
const googleProvider = new GoogleAuthProvider()

/**
 * Convert Firebase User to our FirebaseUser type
 */
const convertFirebaseUser = (user: FirebaseUserType): FirebaseUser => ({
  uid: user.uid,
  email: user.email,
  displayName: user.displayName,
  photoURL: user.photoURL,
  emailVerified: user.emailVerified,
})

/**
 * Convert Firebase error to AuthError
 */
const convertFirebaseError = (error: unknown): AuthError => {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = error.code as string
    const message = 'message' in error ? (error.message as string) : 'Unknown error'

    return {
      code: code.startsWith('auth/') ? (code as AuthError['code']) : 'unknown',
      message,
    }
  }

  return {
    code: 'unknown',
    message: 'An unknown error occurred',
  }
}

/**
 * Firebase Authentication Service
 */
export class FirebaseAuthService {
  /**
   * Sign up with email and password
   */
  async signup(credentials: SignupCredentials): Promise<FirebaseUser> {
    try {
      const auth = getFirebaseAuth()
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      )

      // Update profile with display name if provided
      if (credentials.displayName) {
        await updateProfile(userCredential.user, {
          displayName: credentials.displayName,
        })
      }

      return convertFirebaseUser(userCredential.user)
    } catch (error) {
      throw convertFirebaseError(error)
    }
  }

  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<FirebaseUser> {
    try {
      const auth = getFirebaseAuth()
      const userCredential = await signInWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      )

      return convertFirebaseUser(userCredential.user)
    } catch (error) {
      throw convertFirebaseError(error)
    }
  }

  /**
   * Login with Google OAuth
   */
  async loginWithGoogle(): Promise<FirebaseUser> {
    try {
      const auth = getFirebaseAuth()
      const userCredential = await signInWithPopup(auth, googleProvider)

      return convertFirebaseUser(userCredential.user)
    } catch (error) {
      throw convertFirebaseError(error)
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      const auth = getFirebaseAuth()
      await signOut(auth)
    } catch (error) {
      throw convertFirebaseError(error)
    }
  }

  /**
   * Get current user's ID token
   * @param forceRefresh - Force token refresh
   */
  async getIdToken(forceRefresh = false): Promise<string | null> {
    try {
      const auth = getFirebaseAuth()
      const user = auth.currentUser

      if (!user) {
        return null
      }

      return await user.getIdToken(forceRefresh)
    } catch (error) {
      console.error('Failed to get ID token:', error)
      return null
    }
  }

  /**
   * Get current Firebase user
   */
  getCurrentUser(): FirebaseUser | null {
    const auth = getFirebaseAuth()
    const user = auth.currentUser

    return user ? convertFirebaseUser(user) : null
  }

  /**
   * Subscribe to auth state changes
   * @param callback - Callback function to execute on auth state change
   * @returns Unsubscribe function
   */
  onAuthStateChanged(callback: (user: FirebaseUser | null) => void): Unsubscribe {
    const auth = getFirebaseAuth()

    return onAuthStateChanged(auth, (user) => {
      callback(user ? convertFirebaseUser(user) : null)
    })
  }
}

/**
 * Singleton instance
 */
export const firebaseAuthService = new FirebaseAuthService()
